import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useCourses } from '@/hooks/useCourses'
import { CourseType, type Course } from '@/types/course'
import { Loader2, Clock, BookOpen, Award, Zap } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { HtmlContent } from '@/components/ui/html-content'
import { LevelIcon } from '@/components/ui/LevelIcon'
import { formatDuration } from '@/utils/duration'

const courseTypeIcons: Record<CourseType, React.ReactNode> = {
  regular: <BookOpen className="w-5 h-5" />,
  masterclass: <Award className="w-5 h-5" />,
  workshop: <BookOpen className="w-5 h-5" />,
  trail: <BookOpen className="w-5 h-5" />,
}

const courseTypeLabels: Record<CourseType, string> = {
  regular: 'Curso',
  masterclass: 'Masterclass',
  workshop: 'Workshop',
  trail: 'Trilha',
}

const levelLabels: Record<string, string> = {
  beginner: 'Iniciante',
  intermediate: 'Intermediário',
  advanced: 'Avançado',
  expert: 'Especialista',
}

interface CourseCardProps {
  course: Course
}

function CourseCard({ course }: CourseCardProps) {
  const [imageError, setImageError] = useState(false)

  return (
    <Link
      to={`/cursos/${course.slug}`}
      className="group block bg-card border border-border rounded-lg overflow-hidden hover:border-primary transition-all hover:shadow-lg"
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-muted overflow-hidden relative">
        {course.thumbnail && !imageError ? (
          <img
            src={course.thumbnail}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {courseTypeIcons[course.type]}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Type Badge */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
            {courseTypeLabels[course.type]}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
          {course.title}
        </h3>

        {/* Description */}
        <div className="text-sm text-muted-foreground mb-4 line-clamp-2">
          <HtmlContent content={course.description || ''} />
        </div>

        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          {course.level && (
            <div className="flex items-center gap-1.5">
              <LevelIcon level={course.level} size={18} />
              <span>{levelLabels[course.level]}</span>
            </div>
          )}
          {course.duration && course.duration > 0 && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{formatDuration(course.duration)}</span>
            </div>
          )}
          {/* Masterclass tipicamente é vídeo único — contador de
              módulos não faz sentido (mesma lógica do CourseDetail). */}
          {course.type !== 'masterclass' && (
            <div className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              <span>{course.modules?.length || 0} módulos</span>
            </div>
          )}
        </div>

        {/* Access Type Badge */}
        {course.accessType === 'restricted' && (
          <div className="mt-3 text-xs text-orange-500 font-medium">🔒 Acesso Restrito</div>
        )}
      </div>
    </Link>
  )
}

export default function Courses() {
  const [searchParams] = useSearchParams()
  const type = searchParams.get('type') as CourseType | undefined

  const {
    data: courses,
    isLoading,
    error,
  } = useCourses({
    type,
    status: 'published,coming_soon',
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-2">Erro ao carregar cursos</h2>
          <p className="text-muted-foreground">Tente novamente mais tarde</p>
        </div>
      </div>
    )
  }

  const pageTitle = type
    ? `${courseTypeLabels[type]}${type === 'masterclass' ? 'es' : 's'}`
    : 'Todos os Cursos'

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background py-12">
        <div className="container">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">{pageTitle}</h1>
            <p className="text-muted-foreground">
              {courses?.length || 0}{' '}
              {courses?.length === 1 ? 'curso encontrado' : 'cursos encontrados'}
            </p>
          </div>

          {/* Filtros */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            <Link
              to="/cursos"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                !type
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              Todos
            </Link>
            <Link
              to="/cursos?type=regular"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                type === 'regular'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              Cursos
            </Link>
            <Link
              to="/cursos?type=masterclass"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                type === 'masterclass'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              Masterclasses
            </Link>
          </div>

          {/* Grid de Cursos */}
          {courses && courses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <Clock className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Em breve</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Novos conteúdos incríveis estão chegando. Fique ligado!
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  )
}
